'use client';

import type { LocalCartInfo } from '@/types';
import * as Sentry from '@sentry/nextjs';
import { useAtom, useSetAtom } from 'jotai';
import { atom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useCallback, useRef } from 'react';
import { CART_STORAGE_KEY, cartInfoAtom, readLocalCartInfo } from './internal/cartInfoAtom';

/**
 * ゲストカートの件数を購読する（ヘッダーバッジ表示用）。
 *
 * 件数だけを返すのは意図的。atom は hydration 前に空を返すため、
 * items 配列を公開するとロジックが暫定値を掴んでしまう（GF-2387）。
 * 件数は遅れても購読で自動的に追いつくため、表示用途に限っては安全。
 * @returns localStorage 上のカート件数。hydration 前は 0
 */
const useGuestCartItemCount = (): number => {
  const [cartInfo] = useAtom(cartInfoAtom);
  return cartInfo.items?.length ?? 0;
};

type CartInfoWriter = {
  updateCartInfo: (updater: (current: LocalCartInfo) => LocalCartInfo) => void;
  clearCartInfo: () => void;
};

/**
 * ゲストカートの書き込み口。
 *
 * 書き込みは必ずここを通す。localStorage を直接書くと購読者に通知が飛ばず、
 * ヘッダーバッジが更新されないまま乖離する。
 */
const useCartInfoWriter = (): CartInfoWriter => {
  const setCartInfo = useSetAtom(cartInfoAtom);

  // updater には localStorage の実値を渡す。atom の値は hydration 前や
  // 再レンダーを挟まない連続呼び出しで古くなり、基点にすると既存カートを失う（GF-2387）
  const updateCartInfo = useCallback(
    (updater: (current: LocalCartInfo) => LocalCartInfo) => {
      setCartInfo(updater(readLocalCartInfo()));
    },
    [setCartInfo],
  );

  const clearCartInfo = useCallback(() => {
    setCartInfo(RESET);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [setCartInfo]);

  return { updateCartInfo, clearCartInfo };
};

// ログイン時のカート件数Atom（ヘッダーバッジ表示用）
// SSRで取得した初期値をセットし、クライアント側のカート操作で更新する
const _loginCartCountAtom = atom<number>(0);

const useLoginCartCountAtom = () => {
  return useAtom(_loginCartCountAtom);
};

// グローバルローディング用のAtom
const _globalLoadingAtom = atom(false);

const useGlobalLoadingAtom = () => {
  const [isGlobalLoading, setIsGlobalLoading] = useAtom(_globalLoadingAtom);

  // タイマーIDを保持するためのref
  //NOTE:処理が連続で走ると過去のタイマーがクリアされず爆速でロードが終わってしまう可能性があるのでIDを保持
  //NOTE:各処理で既存のタイマーをクリアする。ユーザーが短時間に複数回操作した場合、古いタイマーが残っていると予期せぬタイミングでローディングが停止してしまう
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startGlobalLoading = useCallback(() => {
    if (isGlobalLoading) return;
    setIsGlobalLoading(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    // 7秒後にローディングを停止
    timerRef.current = setTimeout(() => {
      setIsGlobalLoading(false);
      timerRef.current = null;
    }, 7000);
  }, [isGlobalLoading, setIsGlobalLoading]);

  const stopGlobalLoading = useCallback(() => {
    if (!isGlobalLoading) return;
    setIsGlobalLoading(false);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [isGlobalLoading, setIsGlobalLoading]);
  // WARNING: submit時など、画面遷移を伴わない場合はこちらを使用してください
  const forceStopGlobalLoading = useCallback(() => {
    setIsGlobalLoading(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [setIsGlobalLoading]);

  return [isGlobalLoading, startGlobalLoading, stopGlobalLoading, forceStopGlobalLoading] as const;
};

export { useGuestCartItemCount, useCartInfoWriter, readLocalCartInfo, useLoginCartCountAtom, useGlobalLoadingAtom };
